import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import apiClient from '../../services/api';
import { Category } from '../../types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface CategoryManagerProps {
  categories: Category[];
  refreshCategories: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ categories, refreshCategories }) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.post('/goals/categories/', { name: newCategoryName });
      setNewCategoryName('');
      refreshCategories();
    } catch (err: any) {
      console.error("Failed to add category:", err);
      setError(err.response?.data?.name?.[0] || err.message || 'Failed to add category.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (category: Category) => {
    setEditingCategory(category);
    setEditName(category.name);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;
    if (editName === editingCategory.name) {
      handleCancelEdit();
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.patch(`/goals/categories/${editingCategory.id}/`, { name: editName });
      setEditingCategory(null);
      setEditName('');
      refreshCategories();
    } catch (err: any) {
      console.error("Failed to update category:", err);
      setError(err.response?.data?.name?.[0] || err.message || 'Failed to update category.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.delete(`/goals/categories/${categoryToDelete.id}/`);
      refreshCategories();
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (err: any) {
      console.error("Failed to delete category:", err);
      setError(err.message || 'Failed to delete category.');
      setDeleteDialogOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Categories</CardTitle>
        <CardDescription>Manage your goal and task categories.</CardDescription>
        {error && <p className="text-sm text-red-500 mt-2">Error: {error}</p>}
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 mb-4">
          {categories.length === 0 && (
            <li className="text-muted-foreground text-center py-4">No categories yet.</li>
          )}
          {categories.map(cat => (
             <li key={cat.id} className="flex items-center justify-between p-2 border rounded">
               {editingCategory?.id === cat.id ? (
                 <div className="flex items-center gap-2 flex-1">
                   <Input
                     value={editName}
                     onChange={(e) => setEditName(e.target.value)}
                     autoFocus
                     onKeyDown={(e) => {
                       if (e.key === "Enter") handleSaveEdit();
                       if (e.key === "Escape") handleCancelEdit();
                     }}
                     disabled={isLoading}
                     className="h-8"
                   />
                   <div className="flex gap-1">
                     <Button variant="ghost" size="icon" onClick={handleSaveEdit} disabled={isLoading} className="h-8 w-8" aria-label="Save changes">
                       <Check className="h-4 w-4" />
                     </Button>
                     <Button variant="ghost" size="icon" onClick={handleCancelEdit} disabled={isLoading} className="h-8 w-8" aria-label="Cancel edit">
                       <X className="h-4 w-4" />
                     </Button>
                   </div>
                 </div>
               ) : (
                 <>
                   <span className="flex-1 truncate mr-2">{cat.name}</span>
                   <div className="flex gap-1 flex-shrink-0">
                     <Button variant="ghost" size="icon" onClick={() => handleEditClick(cat)} aria-label={`Edit category ${cat.name}`} className="h-8 w-8"> 
                       <Pencil className="h-4 w-4" />
                     </Button>
                     <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(cat)} aria-label={`Delete category ${cat.name}`} className="h-8 w-8"> 
                       <Trash2 className="h-4 w-4" />
                     </Button>
                   </div>
                 </>
               )}
             </li>
          ))}
        </ul>

        <form onSubmit={handleAddCategory} className="flex space-x-2">
          <Input 
            type="text"
            placeholder="New category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            disabled={isLoading || !!editingCategory}
            required
          />
          <Button type="submit" disabled={isLoading || !!editingCategory}>
            {isLoading ? 'Adding...' : 'Add'}
          </Button>
        </form>
      </CardContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the category "<strong>{categoryToDelete?.name}</strong>". 
              This action cannot be undone. Goals and tasks using this category may be affected (depending on backend logic - e.g., set to null).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)} disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
             >
              {isLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CategoryManager; 